'use client';

import React, { useState } from 'react';
import { Plus, Trash2, Split, AlertCircle, Check } from 'lucide-react';
import { SearchableAccountSelect } from './SearchableAccountSelect';
import type { ExpenseAccountOption } from '@/lib/finance/expenseAccountMapping';

export interface SplitAllocation {
  id: string;
  gl_account_code: string;
  percentage: number;
  description?: string;
}

interface SplitAllocationEditorProps {
  accounts: ExpenseAccountOption[];
  splits: SplitAllocation[];
  onChange: (splits: SplitAllocation[]) => void;
  lineTotal: number;
  isDark?: boolean;
  disabled?: boolean;
}

let splitIdCounter = 0;
function generateSplitId() {
  return `split-${Date.now()}-${++splitIdCounter}`;
}

export function SplitAllocationEditor({
  accounts,
  splits,
  onChange,
  lineTotal,
  isDark = false,
  disabled = false,
}: SplitAllocationEditorProps) {
  const [expanded, setExpanded] = useState(splits.length > 0);

  const totalPercentage = splits.reduce((sum, s) => sum + (Number(s.percentage) || 0), 0);
  const isValid = splits.length === 0 || Math.abs(totalPercentage - 100) < 0.01;

  // Check for mixed account types across splits
  const splitAccountTypes = splits
    .filter(s => s.gl_account_code)
    .map(s => accounts.find(a => a.account_code === s.gl_account_code)?.account_type)
    .filter((t): t is string => !!t);
  const uniqueAccountTypes = [...new Set(splitAccountTypes)];
  const hasMixedAccountTypes = uniqueAccountTypes.length > 1;

  const addSplit = () => {
    const remaining = Math.max(0, 100 - totalPercentage);
    const newSplits = [...splits, { id: generateSplitId(), gl_account_code: '', percentage: remaining, description: '' }];
    onChange(newSplits);
    setExpanded(true);
  };

  const updateSplit = (id: string, field: keyof SplitAllocation, value: string | number) => {
    onChange(splits.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const removeSplit = (id: string) => {
    onChange(splits.filter((s) => s.id !== id));
  };

  const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`;
  const inputClass = `w-full rounded-lg border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${
    isDark ? 'border-gray-600 bg-gray-900 text-gray-100' : 'border-gray-300 bg-white text-gray-900'
  } disabled:opacity-60`;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className={`inline-flex items-center gap-1 text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
        >
          <Split className="h-3.5 w-3.5" />
          Split Allocation
          {splits.length > 0 && (
            <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
              isValid
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
            }`}>
              {splits.length} way{splits.length !== 1 ? 's' : ''}
            </span>
          )}
        </button>
        {!disabled && (
          <button
            type="button"
            onClick={addSplit}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          >
            <Plus className="h-3 w-3" />
            Add Split
          </button>
        )}
      </div>

      {expanded && splits.length > 0 && (
        <div className="space-y-2 rounded-lg border border-gray-200 p-2 dark:border-gray-700">
          {splits.map((split) => {
            const amount = (lineTotal * Number(split.percentage)) / 100;
            return (
              <div key={split.id} className="space-y-1.5">
                <div className="flex items-start gap-2">
                  {/* GL Account */}
                  <div className="flex-1">
                    <SearchableAccountSelect
                      accounts={accounts}
                      value={split.gl_account_code}
                      onChange={(code) => updateSplit(split.id, 'gl_account_code', code)}
                      placeholder="Select GL account"
                      isDark={isDark}
                      disabled={disabled}
                    />
                  </div>

                  {/* Percentage */}
                  <div className="w-20">
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={split.percentage}
                        disabled={disabled}
                        onChange={(e) => updateSplit(split.id, 'percentage', Number(e.target.value))}
                        className={`${inputClass} pr-6`}
                      />
                      <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        %
                      </span>
                    </div>
                  </div>

                  {/* Amount display */}
                  <div className="w-24 text-right">
                    <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      R{amount.toFixed(2)}
                    </div>
                  </div>

                  {/* Remove */}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => removeSplit(split.id)}
                      className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {/* Optional description per split */}
                <input
                  type="text"
                  value={split.description || ''}
                  disabled={disabled}
                  onChange={(e) => updateSplit(split.id, 'description', e.target.value)}
                  placeholder="Split description (optional)"
                  className={`${inputClass} text-xs`}
                />
              </div>
            );
          })}

          {/* Validation summary */}
          <div className="flex items-center justify-between border-t border-gray-100 pt-2 dark:border-gray-700">
            <div className="flex items-center gap-2">
              {isValid ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-500" />
                  <span className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    Total: 100% (R{lineTotal.toFixed(2)})
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                  <span className={`text-xs ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                    Total: {totalPercentage.toFixed(1)}% — must equal 100%
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Mixed account type warning */}
          {hasMixedAccountTypes && (
            <div className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
              <span>This split mixes account types ({uniqueAccountTypes.join(' + ')}) — verify this is intentional for GAAP compliance</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
